/**
 * Admin Authentication Service
 * Separate authentication for admin users only
 */
import { supabase } from './supabaseClient';

class AdminAuthService {
  constructor() {
    this.currentAdmin = null;
    this.currentSession = null;
    this.storageKey = 'bpv_admin_auth_state';
    // Load persisted state asynchronously
    this.loadPersistedState().catch(err => {
      console.error('Error loading persisted state:', err);
    });
  }

  async loadPersistedState() {
    try {
      const persisted = localStorage.getItem(this.storageKey);
      if (persisted) {
        const { admin, session } = JSON.parse(persisted);
        if (session && session.expires_at > Math.floor(Date.now() / 1000)) {
          this.currentAdmin = admin;
          this.currentSession = session;
          
          // Restore Supabase session if needed
          const { data: { session: supabaseSession } } = await supabase.auth.getSession();
          if (!supabaseSession) {
            // Session expired or missing, set it from our stored data
            await supabase.auth.setSession({
              access_token: session.access_token,
              refresh_token: session.refresh_token
            });
          }
        } else {
          localStorage.removeItem(this.storageKey);
        }
      }
    } catch (error) {
      console.error('Failed to load persisted admin state:', error);
      localStorage.removeItem(this.storageKey);
    }
  }

  persistState() {
    try {
      if (this.currentAdmin && this.currentSession) {
        localStorage.setItem(this.storageKey, JSON.stringify({
          admin: this.currentAdmin,
          session: this.currentSession
        }));
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch (error) {
      console.error('Failed to persist admin auth state:', error);
    }
  }

  /**
   * Admin login with email and password
   */
  async loginAdmin(email, password) {
    try {
      if (!email || !password) {
        throw new Error('Please provide both email and password');
      }

      // Try Supabase Auth first
      let authData = null;
      let useSupabaseAuth = false;
      
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email.toLowerCase(),
          password: password
        });
        
        if (!error && data?.user) {
          authData = data;
          useSupabaseAuth = true;
        }
      } catch (authErr) {
        // Supabase auth failed, will try backend API
        console.log('Supabase auth failed, trying backend API...');
      }

      // If Supabase auth failed, try backend API
      if (!useSupabaseAuth) {
        const response = await fetch('http://localhost:4002/api/admin/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.toLowerCase(), password })
        });

        const result = await response.json();
        
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Invalid email or password');
        }

        // Use backend auth response
        const adminProfile = {
          id: result.admin.id,
          email: result.admin.email,
          name: result.admin.name,
          role: result.admin.role,
          is_admin: result.admin.is_admin
        };

        const adminSession = {
          access_token: result.token,
          refresh_token: result.token,
          token_type: 'bearer',
          user: { id: adminProfile.id, email: adminProfile.email },
          expires_at: Math.floor(Date.now() / 1000) + (3600 * 24)
        };

        this.currentAdmin = adminProfile;
        this.currentSession = adminSession;
        this.persistState();

        return adminProfile;
      }

      // If Supabase auth succeeded, continue with profile check
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (profileError || !profile) {
        await supabase.auth.signOut();
        throw new Error('User profile not found');
      }

      // Verify admin role
      if (!profile.is_admin && profile.role !== 'admin' && profile.role !== 'superadmin') {
        await supabase.auth.signOut();
        throw new Error('Access denied. Admin privileges required.');
      }

      const adminProfile = {
        id: profile.id,
        email: profile.email,
        name: profile.name,
        role: profile.role,
        is_admin: profile.is_admin
      };

      const adminSession = {
        access_token: authData.session.access_token,
        refresh_token: authData.session.refresh_token,
        token_type: 'bearer',
        user: { id: adminProfile.id, email: adminProfile.email },
        expires_at: authData.session.expires_at || Math.floor(Date.now() / 1000) + (3600 * 24)
      };

      this.currentAdmin = adminProfile;
      this.currentSession = adminSession;
      this.persistState();

      return adminProfile;
    } catch (error) {
      throw error;
    }
  }

  async logout() {
    try {
      // Try to sign out from Supabase, but don't fail if it errors
      try {
        await supabase.auth.signOut();
      } catch (supabaseError) {
        console.log('Supabase signout skipped:', supabaseError.message);
      }
    } finally {
      // Always clear local state
      this.currentAdmin = null;
      this.currentSession = null;
      localStorage.removeItem(this.storageKey);
      sessionStorage.removeItem('admin_profile_cache');
      
      // Clear all admin-related storage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('admin') || key.includes('supabase'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }

  getCurrentAdmin() {
    return this.currentAdmin;
  }

  getCurrentSession() {
    return this.currentSession;
  }

  isAuthenticated() {
    return !!this.currentAdmin && !!this.currentSession;
  }

  isAdmin() {
    return this.currentAdmin?.is_admin || ['admin', 'superadmin'].includes(this.currentAdmin?.role);
  }
}

const adminAuthService = new AdminAuthService();
export default adminAuthService;
